import React, {createRef} from "react";
import {ServiceBase} from '../ServiceBase';
import {IGameSearchService} from './game-search.service.interface';
import {DomEventSubscription} from '../../../utils/event-listener-registration';
import {action, computed, observable, makeObservable} from 'mobx';
import {
  SearchLinkSuggestion,
  GameSearchResult,
  GameSuggestion,
  ISearchSuggestion,
  SUGGESTED_CATEGORIES
} from './game-search-result';
import {SuggestedLinkConfiguration} from '../configurations/config-data-sections/game-search-box.configuration';
import {Check} from '@sparkware/app-utils';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {GameInfo, gameInfoComparerByTitle} from '../games/game-info';
import {IModalDialogHandler} from "../dialog/dialog.service.interface";
import {GameSearchComponent} from "../../components/game-search/game-search.component";
import {removeSpecialCharacters} from "../../../utils/string-helpers";
import {LIST_TYPE_ID} from "../../../common/const/ai-trending-list-ids-map";
import {IOverlayConsumer} from "../overlay/overlay-consumer.interface";
import {MenuItemKey} from "../configurations/config-data-sections/generated-config-const";
import {IMenuItemAction} from "../menu-item-action-factory/menu-item-action.interface";
import {
  MenuItemDefaultConfigReader
} from "../menu-items-common/menu-item-config-reader/menu-item-default-config-reader";
import {OrbitUserFlowAnalyticsCategory} from "../analytics/analytics.service.interface";

export class GameSearchService extends ServiceBase implements IGameSearchService, IOverlayConsumer {

  private _closeMobileSideMenuOnSearchClose: boolean = false;

  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);

    this.services.overlay.registerConsumer(this);

    this._menuItemConfigReader = new MenuItemDefaultConfigReader(services);


    this.reaction(() => this.services.games.gamesMap,
      () => {
        //this is to avoid circular dependency between this service and ISearchSuggestion implementations
        setTimeout(() => {
          this._resetGamesSearchListCache();
          this.search(this.currentSearchText);
        });

      }, {
      fireImmediately: true
    });

    this.reaction(() => {
      return {
        trendingRecommendedGamesForPlayerByBets: this.services.aiEngine.trendingRecommendedGamesForPlayerByBets,
        aiRecommendedGames: this.services.aiEngine.aiRecommendedGames
      }
    },
      () => {
        if (!this.isSearchInProgress) {
          this._setDefaultSearchResult();
        }
      });
  }

  private readonly _menuItemConfigReader: MenuItemDefaultConfigReader;

  private _miniOrbitSearchBoxDialogHandler: IModalDialogHandler | null = null;

  private _searchInputRef: React.RefObject<HTMLInputElement> = createRef<HTMLInputElement>();
  private _searchComponentRef: React.RefObject<HTMLElement> = createRef<HTMLElement>();
  private _searchResultComponentRef: React.RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

  private _gamesSearchListCache: GameInfo[] | null = null;
  private get gamesSearchList(): GameInfo[] {
    if (!this._gamesSearchListCache) {
      this._gamesSearchListCache = this._buildGamesSearchList()
    }

    return this._gamesSearchListCache;
  }

  getMenuItemAction(key: MenuItemKey): IMenuItemAction {
    return this.services.menuItemActionFactory.getMenuItemAction(key, this._menuItemConfigReader);
  }
  _resetGamesSearchListCache() {
    this._gamesSearchListCache = null;
  }

  private _buildGamesSearchList(): GameInfo[] {
    return Object.values(this.services.games.gamesMap).sort(gameInfoComparerByTitle);
  }

  private _openMiniOrbitSearchDialog(): void {
    this.services.dialog.showModal((dialogHandler) => {
      this._miniOrbitSearchBoxDialogHandler = dialogHandler;
      return (<GameSearchComponent />);
    }, {
      title: ' ',
      onClosed: () => this.closeSearchResult(),
      forceFullscreenTablet: true
    });
  }

  get needsOverlay(): boolean {
    return this.isSearchBoxOpened;
  }

  getOverlayZIndex(): number | undefined {
    return undefined;
  }

  onOverlayClicked() {
    this.closeSearchResult();
  }

  private _openFullOrbitSearchResult() {
    this._disposeDocumentClickSubscription();
    /*
     * Handle search result close on document click
     * Wonder why we use setTimeout? From React 17 the events are bound to react-dom root element instead of window.document
     * Read more here: https://github.com/facebook/react/issues/20636
     */
    setTimeout(() => {
      this._documentClickSubscription = this.services.dom.subscribeTo.click(this._handleClickOutsideSearchComponent);
    });

  }

  get searchInputRef() {
    return this._searchInputRef;
  }

  get searchComponentRef() {
    return this._searchComponentRef;
  }

  get searchResultComponentRef() {
    return this._searchResultComponentRef;
  }

  openSearchResult(): void {
    if (this.isSearchBoxOpened) { //No need to open it if is already opened
      return;
    }

    if (this.services.miniOrbit.isEnabled) {
      this._openMiniOrbitSearchDialog();
    } else {
      this._openFullOrbitSearchResult();
    }

    this._setIsSearchBoxOpened(true);

    this.searchInputRef?.current?.focus();

    if (this.services.device.isMobile) {
      this._closeMobileSideMenuOnSearchClose = !this.services.sideMenu.isMobileMenuOpen;
      if (!this.services.sideMenu.isMobileMenuOpen) {
        this.services.sideMenu.openMobileMenu();
      }
    }

    this.services.analytics.sendOrbitUserFlowEvent("Navigation", this.services.routing.currentRoute?.data?.arenaType || 'Header', 'Search_Open');
  }


  private _documentClickSubscription: DomEventSubscription | null = null;

  private _disposeDocumentClickSubscription() {
    if (this._documentClickSubscription) {
      this._documentClickSubscription.dispose();
      this._documentClickSubscription = null;
    }
  }


  private _closeMiniOrbitSearchDialog = () => {
    if (this._miniOrbitSearchBoxDialogHandler) {
      this._miniOrbitSearchBoxDialogHandler.close();
      this._miniOrbitSearchBoxDialogHandler = null;
    }
  };


  private _closeFullOrbitSearchResult = () => {
    this._disposeDocumentClickSubscription();
  };

  private _handleClickOutsideSearchComponent = (event) => {
    if (this._searchComponentRef.current && !this._searchComponentRef.current.contains(event.target)) {
      this.closeSearchResult();
    }
  }

  closeSearchResult = () => {
    if (!this.isSearchBoxOpened) { //if is already closed don't close it again
      return;
    }
    if (this.services.miniOrbit.isEnabled) {
      this._closeMiniOrbitSearchDialog();
    } else {
      this._closeFullOrbitSearchResult();
    }

    if (this._closeMobileSideMenuOnSearchClose) {
      this._closeMobileSideMenuOnSearchClose = false;
      this.services.sideMenu.closeMobileMenu();
    }

    this._setIsSearchBoxOpened(false);

    //Will called when search box close or when game is selected (And closing the results as well)
    if (this.isSearchInProgress)
      this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.Search, 'Entry', this.currentSearchText);

    this._setCurrentSearchText('');
    this._setDefaultSearchResult();
  };

  private _getGameSuggestion() {
    const gamesSuggestions: GameSuggestion[] = [];
    let aiSortingTypeForAnalytics: string = "byactiveusers";
    let aiGames = this.services.aiEngine.trendingRecommendedGamesForPlayerByActiveUsers;

    if (!(aiGames?.length > 0)) {
      aiSortingTypeForAnalytics = 'config';
      aiGames = this.services.configuration.data.search.suggestedGames;
    }

    aiGames.forEach(gameType => {
      const gameInfo = this.services.games.gamesMap[gameType];

      if (!Check.isNullOrUndefined(gameInfo)) {
        gamesSuggestions.push(new GameSuggestion(this.services, gameInfo, `Suggested_Games_${aiSortingTypeForAnalytics}`, gamesSuggestions.length, aiSortingTypeForAnalytics))
      }
    });

    return gamesSuggestions.slice(0, 6);
  }

  private _setDefaultSearchResult() {
    const quickLinks = this.services.configuration.data.search.quickLinks
      .map(quickLinkConfig => new SearchLinkSuggestion(quickLinkConfig, this.services, 'Quick_Links'));

    this._setSearchResult(new GameSearchResult([], quickLinks, this._getGameSuggestion()));
  }

  @computed
  get isSearchInProgress(): boolean {
    return this.currentSearchText.length >= 2;
  }

  @observable.ref
  isSearchBoxOpened: boolean = false;
  @action.bound
  private _setIsSearchBoxOpened(value: boolean) {
    this.isSearchBoxOpened = value;
  }

  @observable.ref
  currentSearchText: string = '';

  @action.bound
  private _setCurrentSearchText(value: string): void {
    this.currentSearchText = value;
  }

  search(text: string): void {
    text = text || '';

    if (!this.isSearchBoxOpened && text.length > 0) {
      this.openSearchResult();
    }

    this._setCurrentSearchText(text);

    if (this.isSearchInProgress) { //we only search if there are at least 2 characters typed in the search box
      let regEx = new RegExp(removeSpecialCharacters(text), 'gi');
      this._setSearchResult(this._getSearchResult(regEx));
    } else {
      this._setDefaultSearchResult();
    }
  }


  private _getSearchResult(regEx: RegExp): GameSearchResult {
    let games = this._searchForGames(regEx);
    let suggestedCategories: ISearchSuggestion[];
    if (games.length === 0) {
      suggestedCategories = this._getDefaultSuggestedCategories();
    } else {
      suggestedCategories = this._searchForSuggestedCategories(regEx);
    }

    return new GameSearchResult(games, suggestedCategories, this._getGameSuggestion());
  }

  @observable.ref
  searchResult: GameSearchResult;

  @action.bound
  private _setSearchResult(value: GameSearchResult) {
    this.searchResult = value;
  }

  private _searchForGames(regEx: RegExp): ISearchSuggestion[] {

    const result: GameInfo[] = [];
    const gameTypeToGameInfoObj = {};
    const themesIds = this.services.gamesTheme.findThemes(regEx);
    const vendorsIds = this.services.gamesVendor.findVendors(regEx);

    this.gamesSearchList.forEach((gameInfo) => {
      if (removeSpecialCharacters(gameInfo.title).match(regEx)
        || this._matchGameByTheme(gameInfo, themesIds)
        || this._matchGameByVendor(gameInfo, vendorsIds)) {
        result.push(gameInfo);
        gameTypeToGameInfoObj[gameInfo.gameType] = gameInfo;
      }
    });

    const sortedResultGameTypes = this.services.aiEngine.sortGamesByAI(result.map(gameInfo => gameInfo.gameType), LIST_TYPE_ID.ACTIVE_USERS);
    return sortedResultGameTypes.map((gameType, index) => new GameSuggestion(this.services, gameTypeToGameInfoObj[gameType], `Search_Results`, index, 'byactiveusers'));
  }

  private _matchGameByTheme(gameInfo: GameInfo, themesIds: number[]) {
    for (let themeId of gameInfo.themeIds) {
      if (themesIds.includes(themeId)) {
        return true;
      }
    }
    return false;
  }

  private _matchGameByVendor(gameInfo: GameInfo, vendorsIds: number[]) {
    return vendorsIds.includes(gameInfo.vendorId)
  }

  private _searchForSuggestedCategories(regEx: RegExp): ISearchSuggestion[] {
    let suggestedCategoriesOptions = this.services.configuration.data.search.suggestedCategories.suggestedCategoriesOptions;
    if (!suggestedCategoriesOptions) {
      return this._getDefaultSuggestedCategories();
    }

    for (let i = 0; i < suggestedCategoriesOptions.length; i++) {
      let option = suggestedCategoriesOptions[i];
      let result: ISearchSuggestion[] = [];
      if (option.search.match(regEx) && option.categories && option.categories.length > 0) {
        result = option.categories.map(categoryConfig => this._createSuggestedCategory(categoryConfig))
          // TODO: Remove this after updating the Check signatures. Let type inference do the magic
          .filter<ISearchSuggestion>((categorySuggestion): categorySuggestion is ISearchSuggestion => !Check.isNullOrUndefined(categorySuggestion));
      }

      if (result.length > 0) {
        return result;
      }
    }

    return this._getDefaultSuggestedCategories();
  }

  private _getDefaultSuggestedCategories(): ISearchSuggestion[] {
    return this.services.configuration.data.search.suggestedCategories.defaultSuggestedCategories
      .map(suggestedCategory => this._createSuggestedCategory(suggestedCategory))
      // TODO: Remove this after updating the Check signatures. Let type inference do the magic
      .filter<ISearchSuggestion>((suggestion): suggestion is ISearchSuggestion => !Check.isNullOrUndefined(suggestion));
  }

  _createSuggestedCategory(categoryConfig: SuggestedLinkConfiguration): ISearchSuggestion | null {
    if (categoryConfig.gameID) {
      let gameInfo = this.services.games.gamesMap[categoryConfig.gameID];
      if (!gameInfo) {
        this.services.logger.error(`Game type ${categoryConfig.gameID} is in the search suggested categories but this game is not configured to be in this site!`);
        return null;
      }
      return new GameSuggestion(this.services, gameInfo, SUGGESTED_CATEGORIES);
    }
    return new SearchLinkSuggestion(categoryConfig, this.services, SUGGESTED_CATEGORIES);
  }
}
